/**
 * Admin Users List Page
 *
 * Paginated table of all platform users with search, filters and quick
 * actions. Filters (search, plan, status, join-date range) are reflected in
 * the URL query string, so a filtered view can be copied and shared.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  CaretRight as ChevronRight,
  FileText,
  EnvelopeOpen as MailCheck,
  EnvelopeSimple as MailX,
  ArrowsClockwise as RefreshCw,
  MagnifyingGlass as Search,
  Shield,
  SortAscending,
  SortDescending,
  Warning,
  XCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useAdminUsers } from "@/hooks/useAdmin";
import type { AdminUserSummary } from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { cn } from "@/utils/cn";

type SortField = "createdAt" | "lastLoginAt" | "name";
type SortDir = "asc" | "desc";
type PlanFilter = "" | "free" | "pro" | "team" | "enterprise";
type StatusFilter = "" | "active" | "disabled";

const TIER_COLORS: Record<string, string> = {
  free: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
  pro: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  team: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  enterprise:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

const PLAN_OPTIONS: { value: PlanFilter; label: string }[] = [
  { value: "", label: "All plans" },
  { value: "free", label: "Free" },
  { value: "pro", label: "Pro" },
  { value: "team", label: "Team" },
  { value: "enterprise", label: "Enterprise" },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
];

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Empty string reads as "no filter" everywhere below; `undefined` is what the API wants. */
function orUndefined(value: string): string | undefined {
  return value ? value : undefined;
}

interface UsersFilters {
  search: string;
  plan: PlanFilter;
  status: StatusFilter;
  createdFrom: string;
  createdTo: string;
}

const EMPTY_FILTERS: UsersFilters = {
  search: "",
  plan: "",
  status: "",
  createdFrom: "",
  createdTo: "",
};

function filtersFromParams(params: URLSearchParams): UsersFilters {
  const plan = params.get("plan") ?? "";
  const status = params.get("status") ?? "";
  return {
    search: params.get("search") ?? "",
    plan: (PLAN_OPTIONS.some((o) => o.value === plan)
      ? plan
      : "") as PlanFilter,
    status: (STATUS_OPTIONS.some((o) => o.value === status)
      ? status
      : "") as StatusFilter,
    createdFrom: params.get("createdFrom") ?? "",
    createdTo: params.get("createdTo") ?? "",
  };
}

function paramsFromFilters(filters: UsersFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.plan) params.set("plan", filters.plan);
  if (filters.status) params.set("status", filters.status);
  if (filters.createdFrom) params.set("createdFrom", filters.createdFrom);
  if (filters.createdTo) params.set("createdTo", filters.createdTo);
  return params;
}

export function AdminUsersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is the source of truth on load, so a shared/bookmarked link
  // reproduces the same filtered view. `search` gets its own fast-changing
  // state below so typing feels immediate while the query itself is debounced.
  const initialFilters = useMemo(() => filtersFromParams(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [searchInput, setSearchInput] = useState(initialFilters.search);
  const [filters, setFilters] = useState<UsersFilters>(initialFilters);
  const [cursor, setCursor] = useState<string | undefined>();
  const [previousUsers, setPreviousUsers] = useState<AdminUserSummary[]>([]);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const applyFilters = useCallback(
    (next: UsersFilters) => {
      setFilters(next);
      setCursor(undefined);
      setPreviousUsers([]);
      setSearchParams(paramsFromFilters(next), { replace: true });
    },
    [setSearchParams]
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        applyFilters({ ...filters, search: value });
      }, 400);
    },
    [applyFilters, filters]
  );

  // Clean up debounce timer on unmount (U7)
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  function handlePlanChange(value: PlanFilter) {
    applyFilters({ ...filters, plan: value });
  }

  function handleStatusChange(value: StatusFilter) {
    applyFilters({ ...filters, status: value });
  }

  function handleCreatedFromChange(value: string) {
    applyFilters({ ...filters, createdFrom: value });
  }

  function handleCreatedToChange(value: string) {
    applyFilters({ ...filters, createdTo: value });
  }

  function handleClearFilters() {
    setSearchInput("");
    applyFilters(EMPTY_FILTERS);
  }

  const hasActiveFilters =
    filters.search !== "" ||
    filters.plan !== "" ||
    filters.status !== "" ||
    filters.createdFrom !== "" ||
    filters.createdTo !== "";

  const { data, isLoading, error, refetch, isFetching } = useAdminUsers({
    search: orUndefined(filters.search),
    plan: orUndefined(filters.plan),
    status: orUndefined(filters.status) as "active" | "disabled" | undefined,
    createdFrom: orUndefined(filters.createdFrom),
    createdTo: orUndefined(filters.createdTo),
    cursor,
    limit: 25,
  });

  const displayUsers = useMemo(() => {
    const allUsers = [...previousUsers, ...(data?.users ?? [])];
    return [...allUsers].sort((a, b) => {
      let aVal: string | undefined;
      let bVal: string | undefined;
      if (sortField === "name") {
        aVal = (a.name || a.email).toLowerCase();
        bVal = (b.name || b.email).toLowerCase();
      } else {
        aVal = a[sortField] ?? "";
        bVal = b[sortField] ?? "";
      }
      if (sortDir === "asc") return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });
  }, [previousUsers, data?.users, sortField, sortDir]);

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(field === "name" ? "asc" : "desc");
    }
  }

  function handleLoadMore() {
    if (data?.users) {
      setPreviousUsers((prev) => [...prev, ...data.users]);
    }
    setCursor(data?.cursor);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
            Users
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage platform user accounts.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={cn("mr-1.5 h-3.5 w-3.5", isFetching && "animate-spin")}
          />
          Refresh
        </Button>
      </div>

      {/* Search bar + Sort controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by email or name..."
            aria-label="Search users"
            className="w-full rounded-lg border border-border bg-card py-2.5 pl-10 pr-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Sort:</span>
          {(
            [
              { field: "createdAt", label: "Signed Up" },
              { field: "lastLoginAt", label: "Last Login" },
              { field: "name", label: "Name" },
            ] as const
          ).map(({ field, label }) => (
            <button
              key={field}
              onClick={() => toggleSort(field)}
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
                sortField === field
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              {label}
              {sortField === field &&
                (sortDir === "desc" ? (
                  <SortDescending className="h-3 w-3" />
                ) : (
                  <SortAscending className="h-3 w-3" />
                ))}
            </button>
          ))}
        </div>
      </div>

      {/* Filters: plan, status, join-date range */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label
            htmlFor="users-plan-filter"
            className="text-xs font-medium text-muted-foreground"
          >
            Plan
          </label>
          <select
            id="users-plan-filter"
            value={filters.plan}
            onChange={(e) => handlePlanChange(e.target.value as PlanFilter)}
            className="block rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {PLAN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label
            htmlFor="users-status-filter"
            className="text-xs font-medium text-muted-foreground"
          >
            Status
          </label>
          <select
            id="users-status-filter"
            value={filters.status}
            onChange={(e) => handleStatusChange(e.target.value as StatusFilter)}
            className="block rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label
            htmlFor="users-created-from"
            className="text-xs font-medium text-muted-foreground"
          >
            Joined from
          </label>
          <input
            id="users-created-from"
            type="date"
            value={filters.createdFrom}
            max={filters.createdTo || undefined}
            onChange={(e) => handleCreatedFromChange(e.target.value)}
            className="block rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="users-created-to"
            className="text-xs font-medium text-muted-foreground"
          >
            Joined to
          </label>
          <input
            id="users-created-to"
            type="date"
            value={filters.createdTo}
            min={filters.createdFrom || undefined}
            onChange={(e) => handleCreatedToChange(e.target.value)}
            className="block rounded-lg border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            Clear filters
          </Button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center text-sm text-destructive">
          {getErrorMessage(error, "Failed to load users.")}
        </div>
      ) : (
        <>
          {data?.truncated && (
            <p
              role="status"
              className="flex items-center gap-2 rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-600/30 dark:bg-amber-900/10 dark:text-amber-200"
            >
              <Warning className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Showing partial results — the scan stopped early. Load more to
              keep going.
            </p>
          )}

          <div className="overflow-x-auto rounded-xl border border-border/50">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">User</th>
                  <th className="hidden px-4 py-3 sm:table-cell">Plan</th>
                  <th className="hidden px-4 py-3 md:table-cell">Posts</th>
                  <th className="hidden px-4 py-3 md:table-cell">Verified</th>
                  <th className="hidden px-4 py-3 lg:table-cell">Signed Up</th>
                  <th className="hidden px-4 py-3 xl:table-cell">Last Login</th>
                  <th className="px-4 py-3 text-center">Access</th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {displayUsers.map((user) => (
                  <tr
                    key={user.userId}
                    role="link"
                    tabIndex={0}
                    className="cursor-pointer transition-colors hover:bg-muted/20"
                    onClick={() => navigate(`/users/${user.userId}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/users/${user.userId}`);
                      }
                    }}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium">
                          {user.name || user.email}
                          {user.roles.length > 0 && (
                            <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                              <Shield className="h-2.5 w-2.5" />
                              {user.roles[0]?.replace("platform_", "")}
                            </span>
                          )}
                        </p>
                        {user.name && (
                          <p className="text-xs text-muted-foreground">
                            {user.email}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 sm:table-cell">
                      <span
                        className={cn(
                          "inline-block rounded-full px-2.5 py-0.5 text-xs font-medium",
                          TIER_COLORS[user.plan] ?? TIER_COLORS.free
                        )}
                      >
                        {user.plan}
                      </span>
                    </td>
                    <td className="hidden px-4 py-3 md:table-cell">
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <FileText className="h-3.5 w-3.5" />
                        {user.totalPublishedPosts}
                      </span>
                    </td>
                    <td className="hidden px-4 py-3 md:table-cell">
                      {user.emailVerified ? (
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <MailCheck className="h-3.5 w-3.5" />
                          <span className="text-xs">Yes</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-500">
                          <MailX className="h-3.5 w-3.5" />
                          <span className="text-xs">No</span>
                        </span>
                      )}
                    </td>
                    <td className="hidden px-4 py-3 text-muted-foreground lg:table-cell">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="hidden px-4 py-3 text-muted-foreground xl:table-cell">
                      {formatDate(user.lastLoginAt)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {user.isActive ? (
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-3.5 w-3.5" />
                          <span className="hidden text-xs sm:inline">
                            Enabled
                          </span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-500">
                          <XCircle className="h-3.5 w-3.5" />
                          <span className="hidden text-xs sm:inline">
                            Disabled
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </td>
                  </tr>
                ))}

                {displayUsers.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      {hasActiveFilters
                        ? "No users match your filters."
                        : "No users found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data?.cursor && (
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={handleLoadMore}>
                Load More
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
